import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { trackPageView } from '@/lib/analytics'
import { ThemeProvider } from '@/theme/ThemeProvider'
import { AuthProvider, useAuth } from '@/auth/AuthProvider'
import { DiaryProvider } from '@/diary/DiaryProvider'
import { AppShell } from '@/components/AppShell'
import { FullScreenLoader } from '@/components/FullScreenLoader'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { SignIn } from '@/routes/SignIn'

// Code-split the authenticated app so sign-in loads fast.
const Diary = lazy(() => import('@/routes/Diary').then((m) => ({ default: m.Diary })))
const Log = lazy(() => import('@/routes/Log').then((m) => ({ default: m.Log })))
const SavedFoods = lazy(() => import('@/routes/SavedFoods').then((m) => ({ default: m.SavedFoods })))
const Recipes = lazy(() => import('@/routes/Recipes').then((m) => ({ default: m.Recipes })))
const RecipeDetail = lazy(() => import('@/routes/RecipeDetail').then((m) => ({ default: m.RecipeDetail })))
const Progress = lazy(() => import('@/routes/Progress').then((m) => ({ default: m.Progress })))
const History = lazy(() => import('@/routes/History').then((m) => ({ default: m.History })))
const Report = lazy(() => import('@/routes/Report').then((m) => ({ default: m.Report })))
const Settings = lazy(() => import('@/routes/Settings').then((m) => ({ default: m.Settings })))

/** Public routes (sign-in): bounce already-authed users into the app. */
function PublicOnly() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (user) {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from || '/diary'} replace />
  }
  return <Outlet />
}

/** First-run account setup (legacy migration / default profile creation). */
function SetupGate() {
  const { t } = useTranslation()
  const { setupState, retrySetup } = useAuth()
  if (setupState === 'failed') {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-md bg-bg p-lg text-center">
        <h1 className="text-h3 font-bold text-text">
          {t('legacyMigration.errorTitle', 'Setup didn’t finish')}
        </h1>
        <p className="max-w-sm text-body text-text-secondary">
          {t('legacyMigration.errorBody', 'We couldn’t finish setting up your account. Check your connection and try again.')}
        </p>
        <button
          onClick={retrySetup}
          className="rounded-lg bg-primary px-lg py-sm text-button font-semibold text-white hover:opacity-90"
        >
          {t('common.retry', 'Retry')}
        </button>
      </div>
    )
  }
  return <FullScreenLoader label={t('legacyMigration.progress', 'Setting up your account…')} />
}

/**
 * Authed app — freemium, mirror of mobile: everyone gets the full tracker;
 * premium only gates extras (micros display, CSV export, unlimited scans —
 * scans are enforced SERVER-side).
 */
function RequireAuth() {
  const { user, userProfile, loading, profileLoading } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
  // No profile yet → run first-time setup (legacy migration / defaults).
  if (!userProfile) {
    if (profileLoading) return <FullScreenLoader />
    return <SetupGate />
  }
  return (
    <DiaryProvider>
      <AppShell>
        <Suspense fallback={<FullScreenLoader />}>
          <Outlet />
        </Suspense>
      </AppShell>
    </DiaryProvider>
  )
}

// GA4 SPA page views — one per route change.
function PageViewTracker() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname)
  }, [location.pathname])
  return null
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <PageViewTracker />
            <Routes>
              <Route element={<PublicOnly />}>
                <Route path="/sign-in" element={<SignIn />} />
              </Route>

              <Route element={<RequireAuth />}>
                <Route path="/diary" element={<Diary />} />
                <Route path="/log" element={<Log />} />
                <Route path="/saved-foods" element={<SavedFoods />} />
                <Route path="/recipes" element={<Recipes />} />
                <Route path="/recipes/:recipeId" element={<RecipeDetail />} />
                <Route path="/progress" element={<Progress />} />
                <Route path="/history" element={<History />} />
                <Route path="/report" element={<Report />} />
                <Route path="/settings" element={<Settings />} />
              </Route>

              <Route path="/" element={<Navigate to="/diary" replace />} />
              <Route path="*" element={<Navigate to="/diary" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
